// FILE: /src/services/apps.js
import axiosServices from "./axios";

/**
 * Apps are now queue-based:
 * - Run endpoints return HTTP 202 with { queued: true, status: "processing", transactionId, jobId, queueName, transaction }
 * - Final output is written later to the AppTransaction document.
 */

export const APP_TRANSACTION_TERMINAL_STATUSES = ["completed", "failed", "refunded"];
export const APP_TRANSACTION_ACTIVE_STATUSES = ["processing"];
export const APP_TRANSACTION_TERMINAL_EVENT =
  "oolook:app-transaction-terminal";

const notifiedTerminalTransactions =
  new Set();

const notifyTerminalAppTransaction = (
  transaction
) => {
  if (
    typeof window ===
      "undefined" ||
    !isTerminalAppTransaction(
      transaction
    )
  ) {
    return;
  }

  const transactionId =
    transaction?._id ||
    transaction?.id ||
    null;

  if (
    !transactionId ||
    notifiedTerminalTransactions
      .has(
        String(
          transactionId
        )
      )
  ) {
    return;
  }

  notifiedTerminalTransactions.add(
    String(
      transactionId
    )
  );

  window.dispatchEvent(
    new CustomEvent(
      APP_TRANSACTION_TERMINAL_EVENT,
      {
        detail: {
          transactionId:
            String(
              transactionId
            ),
          status:
            transaction.status,
          copilotSessionId:
            transaction
              .copilotSessionId ||
            transaction
              .metadata
              ?.copilot
              ?.sessionId ||
            null,
          copilotPendingActionId:
            transaction
              .copilotPendingActionId ||
            transaction
              .metadata
              ?.copilot
              ?.pendingActionId ||
            null,
        },
      }
    )
  );
};

const DEFAULT_POLL_INTERVAL_MS = 3000;
const DEFAULT_POLL_TIMEOUT_MS = 15 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Normalize backend errors so Apps UI can show consistent messages.
 */
const normalizeAppsError = (error, fallbackMessage = "Apps request failed.") => {
  if (
    error.response?.status === 403 &&
    error.response?.data?.errorType === "LIMIT_REACHED"
  ) {
    const limitError = new Error(
      error.response.data.message ||
        error.response.data.error ||
        "You do not have enough credits for this action."
    );

    limitError.isLimitError = true;
    limitError.errorType = "LIMIT_REACHED";
    limitError.feature = error.response.data.feature || "Apps Credits";
    limitError.requiredCredits = error.response.data.requiredCredits;
    limitError.remainingCredits = error.response.data.remainingCredits;

    return limitError;
  }

  const normalError = new Error(
    error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      fallbackMessage
  );

  normalError.statusCode = error.response?.status;
  normalError.transactionId = error.response?.data?.transactionId;
  normalError.creditsRefunded = error.response?.data?.creditsRefunded;
  normalError.refundError = error.response?.data?.refundError;

  return normalError;
};

const getTransactionIdFromPayload = (payload = {}) => {
  return (
    payload.transactionId ||
    payload.transaction?._id ||
    payload.transaction?.id ||
    payload.result?.transactionId ||
    payload.result?.transaction?._id ||
    null
  );
};

const getJobIdFromPayload = (payload = {}) => {
  return (
    payload.jobId ||
    payload.bullJobId ||
    payload.transaction?.bullJobId ||
    payload.transaction?.metadata?.queue?.jobId ||
    payload.transaction?.metadata?.queue?.bullJobId ||
    null
  );
};

const getQueueNameFromPayload = (payload = {}) => {
  return (
    payload.queueName ||
    payload.transaction?.queueName ||
    payload.transaction?.metadata?.queue?.queueName ||
    null
  );
};

export const isQueuedAppResponse = (payload = {}) => {
  return (
    payload?.queued === true ||
    payload?.status === "processing" ||
    Boolean(getTransactionIdFromPayload(payload))
  );
};

export const isTerminalAppTransaction = (transaction = {}) => {
  return APP_TRANSACTION_TERMINAL_STATUSES.includes(
    String(transaction?.status || "").toLowerCase()
  );
};

export const isActiveAppTransaction = (transaction = {}) => {
  return APP_TRANSACTION_ACTIVE_STATUSES.includes(
    String(transaction?.status || "").toLowerCase()
  );
};

/**
 * Ensures all run endpoint responses expose a stable shape.
 */
export const normalizeQueuedAppResponse = (payload = {}) => {
  const transactionId = getTransactionIdFromPayload(payload);
  const jobId = getJobIdFromPayload(payload);
  const queueName = getQueueNameFromPayload(payload);

  return {
    ...payload,
    queued: payload.queued !== undefined ? payload.queued : Boolean(transactionId),
    status: payload.status || payload.transaction?.status || "processing",
    transactionId,
    jobId,
    queueName,
    transaction: payload.transaction || null,
    credits: payload.credits || null,
  };
};

/**
 * GET /api/apps/catalog
 *
 * Returns:
 * {
 *   success: true,
 *   apps: [],
 *   credits: {
 *     totalCredits,
 *     consumedCredits,
 *     remainingCredits
 *   }
 * }
 */
export const fetchAppsCatalog = async () => {
  try {
    const response = await axiosServices.get("/api/apps/catalog");
    return response.data;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to fetch apps catalog.");
  }
};



/**
 * GET /api/apps/image-generate/models
 *
 * Returns only currently available models from the Python provider.
 * The selected `model` should be passed exactly as returned.
 */
export const fetchImageGenerateModels = async () => {
  try {
    const response = await axiosServices.get("/api/apps/image-generate/models");

    const models = Array.isArray(response.data?.models)
      ? response.data.models
      : [];

    return models
      .map((item) => {
        if (typeof item === "string") {
          return {
            model: item,
            label: item,
            raw: item,
          };
        }

        const model =
          item?.model ||
          item?.name ||
          item?.id ||
          item?.value ||
          item?.label ||
          null;

        if (!model) return null;

        return {
          ...item,
          model: String(model),
          label: item.label || String(model),
          raw: item,
        };
      })
      .filter(Boolean);
  } catch (error) {
    throw normalizeAppsError(error, "Failed to fetch image generation models.");
  }
};
/**
 * GET /api/apps/history
 */
export const fetchAppsHistory = async ({
  groupId,
  appId,
  status,
  page = 1,
  limit = 20,
} = {}) => {
  try {
    const response = await axiosServices.get("/api/apps/history", {
      params: {
        groupId,
        appId,
        status,
        page,
        limit,
      },
    });

    return response.data;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to fetch apps history.");
  }
};

/**
 * GET /api/apps/credits/estimate
 */
export const estimateAppCredits = async ({ appId, quantity = 1 }) => {
  try {
    const response = await axiosServices.get("/api/apps/credits/estimate", {
      params: {
        appId,
        quantity,
      },
    });

    return response.data?.estimate;
  } catch (error) {
    throw normalizeAppsError(error, "Failed to estimate app credits.");
  }
};

/**
 * GET /api/apps/transactions/:transactionId
 */
export const fetchAppTransaction = async (transactionId) => {
  try {
    if (!transactionId) {
      throw new Error("transactionId is required.");
    }

    const response = await axiosServices.get(
      `/api/apps/transactions/${transactionId}`
    );

    const transaction =
      response.data?.transaction;

    notifyTerminalAppTransaction(
      transaction
    );

    return transaction;
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Failed to fetch app transaction.");
    }

    throw error;
  }
};

/**
 * Polls a transaction once per interval until completed/failed/refunded.
 */
export const waitForAppTransaction = async (
  transactionId,
  {
    intervalMs = DEFAULT_POLL_INTERVAL_MS,
    timeoutMs = DEFAULT_POLL_TIMEOUT_MS,
    onUpdate = null,
    shouldContinue = null,
  } = {}
) => {
  if (!transactionId) {
    throw new Error("transactionId is required.");
  }

  const startedAt = Date.now();
  let lastTransaction = null;

  while (Date.now() - startedAt < timeoutMs) {
    if (typeof shouldContinue === "function" && shouldContinue() === false) {
      return lastTransaction;
    }

    const transaction = await fetchAppTransaction(transactionId);
    lastTransaction = transaction;

    if (typeof onUpdate === "function") {
      onUpdate(transaction);
    }

    if (isTerminalAppTransaction(transaction)) {
      return transaction;
    }

    await sleep(intervalMs);
  }

  const timeoutError = new Error(
    "App job is still processing. Please check Activity later."
  );
  timeoutError.transactionId = transactionId;
  timeoutError.lastTransaction = lastTransaction;

  throw timeoutError;
};

export const pollAppTransaction = waitForAppTransaction;

/**
 * Convenience helper for components:
 * accepts the raw response from a run endpoint, extracts transactionId,
 * then waits until terminal status.
 */
export const waitForQueuedAppResponse = async (
  runResponse,
  options = {}
) => {
  const normalized = normalizeQueuedAppResponse(runResponse);
  if (!normalized.transactionId) {
    return normalized.transaction || normalized;
  }

  return waitForAppTransaction(normalized.transactionId, options);
};

/**
 * POST /api/apps/background-remove
 *
 * Supports:
 * - File upload
 * - Existing image/video URL
 */
export const runBackgroundRemover = async ({
  groupId,
  file = null,
  imageUrl = null,
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!file && !imageUrl) {
      throw new Error(
        "Please upload an image/video or select one from your media library."
      );
    }

    const isVideoFile =
      file && String(file.type || "").toLowerCase().startsWith("video/");
    const isVideoUrl = /\.(mp4|mov|webm)(\?|$)/i.test(
      String(imageUrl || "").toLowerCase()
    );

    let response;

    if (file) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append(isVideoFile ? "video" : "image", file);

      response = await axiosServices.post(
        "/api/apps/background-remove",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      response = await axiosServices.post("/api/apps/background-remove", {
        groupId,
        ...(isVideoUrl ? { videoUrl: imageUrl } : { imageUrl }),
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Background removal failed.");
    }

    throw error;
  }
};

export const runCaptionGenerator = async ({
  groupId,
  prompt = "",
  caption = "",
  files = [],
  urls = [],
  useBrandKit = true,
  options = {},
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    const hasFiles = Array.isArray(files) && files.length > 0;
    let response;

    if (hasFiles) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("prompt", prompt || "");
      formData.append("caption", caption || "");
      formData.append("useBrandKit", String(Boolean(useBrandKit)));
      formData.append("options", JSON.stringify(options || {}));

      if (Array.isArray(urls) && urls.length > 0) {
        formData.append("urls", JSON.stringify(urls));
      }

      files.forEach((file) => {
        formData.append("files", file);
      });

      response = await axiosServices.post(
        "/api/apps/caption-generate",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      response = await axiosServices.post("/api/apps/caption-generate", {
        groupId,
        prompt,
        caption,
        urls,
        useBrandKit,
        options,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Caption generation failed.");
    }

    throw error;
  }
};

export const runCaptionVariants = async ({
  groupId,
  caption,
  expand = false,
  generate_similar = false,
  n = 1,
  useBrandKit = true,
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!caption) {
      throw new Error("caption is required.");
    }

    const response = await axiosServices.post("/api/apps/caption-variants", {
      groupId,
      caption,
      expand,
      generate_similar,
      n,
      useBrandKit,
    });

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Caption variant generation failed.");
    }

    throw error;
  }
};

export const runImageColorPalette = async ({
  groupId,
  file = null,
  imageUrl = null,
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!file && !imageUrl) {
      throw new Error("Please upload an image or select one from your media library.");
    }

    let response;

    if (file) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("image", file);

      response = await axiosServices.post(
        "/api/apps/image-color-palette",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      response = await axiosServices.post("/api/apps/image-color-palette", {
        groupId,
        imageUrl,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Image color palette extraction failed.");
    }

    throw error;
  }
};

export const runImageResizer = async ({
  groupId,
  file = null,
  imageUrl = null,
  aspectRatio = "1:1",
  instruction = "",
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!file && !imageUrl) {
      throw new Error("Please upload an image or select one from your media library.");
    }

    if (!aspectRatio) {
      throw new Error("Please select an aspect ratio.");
    }

    let response;

    if (file) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("image", file);
      formData.append("aspect_ratio", aspectRatio);
      formData.append("instruction", instruction || "");

      response = await axiosServices.post("/api/apps/image-resize", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/image-resize", {
        groupId,
        imageUrl,
        aspectRatio,
        instruction,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Image resizing failed.");
    }

    throw error;
  }
};

export const runImageUpscaler = async ({
  groupId,
  file = null,
  imageUrl = null,
  videoUrl = null,
  scale = 4,
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    const inputUrl = videoUrl || imageUrl;

    if (!file && !inputUrl) {
      throw new Error(
        "Please upload an image/video or select one from your media library."
      );
    }

    const isVideoFile =
      file && String(file.type || "").toLowerCase().startsWith("video/");
    const isVideoUrl = /\.(mp4|mov|webm)(\?|$)/i.test(
      String(inputUrl || "").toLowerCase()
    );

    let response;

    if (file) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append(isVideoFile ? "video" : "image", file);
      formData.append("scale", String(scale));

      response = await axiosServices.post("/api/apps/image-upscale", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/image-upscale", {
        groupId,
        ...(isVideoUrl ? { videoUrl: inputUrl } : { imageUrl: inputUrl }),
        scale,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Upscaling failed.");
    }

    throw error;
  }
};

export const runImageColorGrade = async ({
  groupId,
  file = null,
  imageUrl = null,
  colors = [],
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!file && !imageUrl) {
      throw new Error("Please upload an image or select one from your media library.");
    }

    if (!Array.isArray(colors) || colors.length === 0) {
      throw new Error("Please select at least one color.");
    }

    let response;

    if (file) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("image", file);
      formData.append("colors", JSON.stringify(colors));

      response = await axiosServices.post(
        "/api/apps/image-color-grade",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } else {
      response = await axiosServices.post("/api/apps/image-color-grade", {
        groupId,
        imageUrl,
        colors,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Image color grading failed.");
    }

    throw error;
  }
};

export const runMoodboard = async ({
  groupId,
  files = [],
  imageUrls = [],
  brandContext = "",
  useBrandKit = true,
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const normalizedImageUrls = Array.isArray(imageUrls)
      ? imageUrls.map((url) => String(url || "").trim()).filter(Boolean)
      : [];

    if (normalizedFiles.length === 0 && normalizedImageUrls.length === 0) {
      throw new Error(
        "Please upload images, select images from your media library, or provide image URLs."
      );
    }

    let response;

    if (normalizedFiles.length > 0) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("imageUrls", JSON.stringify(normalizedImageUrls));
      formData.append("brandContext", brandContext || "");
      formData.append("useBrandKit", String(Boolean(useBrandKit)));

      normalizedFiles.forEach((file) => {
        formData.append("images", file);
      });

      response = await axiosServices.post("/api/apps/moodboard", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/moodboard", {
        groupId,
        imageUrls: normalizedImageUrls,
        brandContext,
        useBrandKit,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Moodboard generation failed.");
    }

    throw error;
  }
};

export const runImageGenerator = async ({
  groupId,
  prompt,
  model,
  aspectRatio = "1:1",
  resolution = "1K",
  n = 1,
  files = [],
  imageUrls = [],
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!prompt || !String(prompt).trim()) {
      throw new Error("Please enter a prompt.");
    }

    if (!model) {
      throw new Error("Please select a model.");
    }

    const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const normalizedImageUrls = Array.isArray(imageUrls)
      ? imageUrls.map((url) => String(url || "").trim()).filter(Boolean)
      : [];

    let response;

    if (normalizedFiles.length > 0) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("prompt", prompt);
      formData.append("model", model);
      formData.append("aspect_ratio", aspectRatio);
      formData.append("resolution", resolution);
      formData.append("n", String(n));

      if (normalizedImageUrls.length > 0) {
        formData.append("imageUrls", JSON.stringify(normalizedImageUrls));
      }

      normalizedFiles.forEach((file) => {
        formData.append("images", file);
      });

      response = await axiosServices.post("/api/apps/image-generate", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/image-generate", {
        groupId,
        prompt,
        model,
        aspectRatio,
        resolution,
        n,
        imageUrls: normalizedImageUrls,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Image generation failed.");
    }

    throw error;
  }
};

export const runCarouselMaker = async ({
  groupId,
  topic,
  cardCount = 8,
  platform = "Instagram",
  aspectRatio = "4:5",
  model = "Oolook Aura",
  url = "",
  useWebsiteImages = false,
  useBrandKit = true,
  selectedStyleKey = "",
  selectedStyleText = "",
  files = [],
  referenceImageUrls = [],
}) => {
  try {
    if (!groupId) {
      throw new Error("groupId is required.");
    }

    if (!topic || !String(topic).trim()) {
      throw new Error("Please enter a carousel topic.");
    }

    const normalizedFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const normalizedReferenceImageUrls = Array.isArray(referenceImageUrls)
      ? referenceImageUrls.map((item) => String(item || "").trim()).filter(Boolean)
      : [];

    let response;

    if (normalizedFiles.length > 0) {
      const formData = new FormData();
      formData.append("groupId", groupId);
      formData.append("topic", topic);
      formData.append("card_count", String(cardCount));
      formData.append("platform", platform);
      formData.append("aspect_ratio", aspectRatio);
      formData.append("model", model);
      formData.append("url", url || "");
      formData.append("use_website_images", String(Boolean(useWebsiteImages)));
      formData.append("useBrandKit", String(Boolean(useBrandKit)));
      formData.append("selectedStyleKey", selectedStyleKey || "");
      formData.append("selectedStyleText", selectedStyleText || "");

      if (normalizedReferenceImageUrls.length > 0) {
        formData.append(
          "referenceImageUrls",
          JSON.stringify(normalizedReferenceImageUrls)
        );
      }

      normalizedFiles.forEach((file) => {
        formData.append("reference_images", file);
      });

      response = await axiosServices.post("/api/apps/carousel-maker", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } else {
      response = await axiosServices.post("/api/apps/carousel-maker", {
        groupId,
        topic,
        cardCount,
        platform,
        aspectRatio,
        model,
        url,
        useWebsiteImages,
        useBrandKit,
        selectedStyleKey,
        selectedStyleText,
        referenceImageUrls: normalizedReferenceImageUrls,
      });
    }

    return normalizeQueuedAppResponse(response.data);
  } catch (error) {
    if (error.response) {
      throw normalizeAppsError(error, "Carousel generation failed.");
    }

    throw error;
  }
};

const appsService = {
APP_TRANSACTION_TERMINAL_EVENT,
fetchAppsCatalog,
fetchAppsHistory,
estimateAppCredits,
  fetchAppTransaction,
  waitForAppTransaction,
  pollAppTransaction,
  waitForQueuedAppResponse,
  normalizeQueuedAppResponse,
  isQueuedAppResponse,
  isTerminalAppTransaction,
  isActiveAppTransaction,
  runBackgroundRemover,
  runCaptionGenerator,
  runCaptionVariants,
  runImageResizer,
  runImageUpscaler,
  runImageColorPalette,
  runImageColorGrade,
  runMoodboard,
  runImageGenerator,
  runCarouselMaker,
  fetchImageGenerateModels,
};

export default appsService;